/**
 * Problems tab — every KJV-vs-NASB problem verse as an accordion list,
 * most severe first, with a search filter on top. Expanding an item
 * lazy-loads its full analysis; "Open analysis" jumps the reader to the
 * verse and opens the analysis panel.
 */

import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { ProblemListEntry } from '@skjv/common';
import { bibleState } from '@/state/bible-state';
import { fetchProblemList } from '@/api/verse-analysis';
import { EmptyState } from '@components/common';
import { WarningIcon, LoadingSpinner } from '@components/icons';
import { ProblemAccordionItem } from './problem-accordion-item';

const VISIBLE_STEP = 100;

export const ProblemsContent = observer(function ProblemsContent() {
  const [problems, setProblems] = useState<ProblemListEntry[] | null>(null);
  const [hasError, setHasError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(VISIBLE_STEP);

  useEffect(() => {
    fetchProblemList()
      .then(setProblems)
      .catch(() => setHasError(true));
  }, []);

  const filtered = useMemo(() => {
    if (!problems) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return problems;
    return problems.filter(
      (p) =>
        p.reference.toLowerCase().includes(q) || p.shortSummary.toLowerCase().includes(q),
    );
  }, [problems, searchQuery]);

  const visible = filtered.slice(0, visibleCount);

  if (hasError) {
    return (
      <EmptyState
        icon={<WarningIcon className="h-8 w-8" />}
        title="Problem list unavailable"
        hint="Check that the API is running, then reopen this tab."
      />
    );
  }

  if (!problems) {
    return (
      <div className="flex items-center gap-3 py-6 text-stone-500 dark:text-stone-400">
        <LoadingSpinner className="h-5 w-5" />
        <span>Loading problems…</span>
      </div>
    );
  }

  return (
    <>
      <div className="mb-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setVisibleCount(VISIBLE_STEP);
          }}
          placeholder={`Search ${problems.length.toLocaleString()} problems…`}
          className="bookmark-input"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<WarningIcon className="h-8 w-8" />}
          title="No matches"
          hint="Try a reference (e.g. 1 John 5:7) or a word from the summary."
        />
      ) : (
        <div className="problems-list">
          {visible.map((problem) => {
            const key = `${problem.testament}-${problem.book}-${problem.chapter}-${problem.verse}`;
            return (
              <ProblemAccordionItem
                key={key}
                problem={problem}
                isExpanded={expandedKey === key}
                onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
                onOpenAnalysis={() => {
                  const location = {
                    testamentIndex: problem.testament - 1,
                    bookIndex: problem.book - 1,
                    chapterIndex: problem.chapter - 1,
                    verseIndex: problem.verse - 1,
                  };
                  bibleState.setCurrentLocation(location);
                  bibleState.openVerseAnalysisAt(location);
                }}
              />
            );
          })}
          {filtered.length > visibleCount && (
            <button
              className="problems-show-more"
              onClick={() => setVisibleCount(visibleCount + VISIBLE_STEP)}
            >
              Show more ({(filtered.length - visibleCount).toLocaleString()} remaining)
            </button>
          )}
        </div>
      )}
    </>
  );
});
